import { Component, ElementRef, OnInit, ViewChild, OnDestroy, AfterViewInit, NgZone, ChangeDetectorRef } from '@angular/core';
import { PageBase, LanguageInfo, TranslationInfo, TranslationField } from 't4core';
import { style } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { CanComponentDeactivate } from '../../../../t4common/guards/pending-changes.guard';
import { take } from 'rxjs/operators';



@Component({
  styleUrls: ['./manage-translations.css'],
  selector: 't4-manage-translations',
  templateUrl: './manage-translations.html',
})
export class ManageTranslationsComponent extends PageBase implements OnInit, AfterViewInit, OnDestroy {
  public languages: LanguageInfo[] = [];
  public translationInfo: TranslationInfo[] = [];
  public filteredTranslationInfo: TranslationInfo[] = [];
  public isLoading: boolean = false;
  public translationsChanged: boolean = false;
  public language: string = 'en';
  public onlyUnverified = new FormControl(false);
  public modifiedKeys = new Set<string>();
  public translationsColWidth = 720;
  private ro?: ResizeObserver;

  @ViewChild('translationsHeader', { read: ElementRef, static: false })
  translationsHeader?: ElementRef<HTMLElement>;

  constructor(el: ElementRef, private cdr: ChangeDetectorRef, private zone: NgZone) {
    super(el, "ManageTranslations");

  }

  public async ngOnInit() {
    window.addEventListener('beforeunload', this.beforeUnloadListener);
    await this.getLanguages();
    await this.getAllTranslations();    
  }

  ngAfterViewInit(): void {
    this.zone.onStable.subscribe(() => this.attachObserver());
  }

  private attachObserver(): void {
    const el = this.translationsHeader?.nativeElement;
    if (!el || this.ro) return;

    const update = (w: number) => {
      const next = Math.floor(w);
      if (next !== this.translationsColWidth) {
        this.translationsColWidth = next;
        this.cdr.markForCheck();
      }
    };

    update(el.getBoundingClientRect().width);

    this.ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? el.getBoundingClientRect().width;
      update(w);
    });
    this.ro.observe(el);
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadListener);
    this.ro?.disconnect();
  }

  beforeUnloadListener = (event: BeforeUnloadEvent) => {
    if (this.translationsChanged) {
      event.preventDefault();
      event.returnValue = ''; 
    }
  };

  public async getLanguages() {
    this.isLoading = true;
    this.languages = await this.Api.get<LanguageInfo[]>("Settings/GetLanguages");
    this.isLoading = false;
  }

  public async getAllTranslations() {
    this.isLoading = true;
    this.translationInfo = await this.Api.get<TranslationInfo[]>("Settings/GetAllTranslations");
    await this.updateFilter();
    this.isLoading = false;
  }

  public async updateFilter() {
    const onlyUnverifiedChecked = this.onlyUnverified.value;

    //If the only unverified is not checked, then no filtering is required
    if (!onlyUnverifiedChecked) {
      this.filteredTranslationInfo = [...this.translationInfo];
      return;
    }

    //If the only unverfied is checked, then filter out only the unverfied translations.
    this.filteredTranslationInfo = this.translationInfo.map(info => {
        const unverifiedFields = info.TranslationFields.filter(f => !f.IsVerified);
        if (unverifiedFields.length === 0) {
          return null;
        }

        return {
          ...info,
          TranslationFields: unverifiedFields
        };
      }).filter(Boolean);
  }

  public async onLanguageChange(code: string) {
    this.language = code;
  }

  public async save() {
    const updates = this.translationInfo
      .filter(t => this.modifiedKeys.has(t.Key));

    if (updates.length === 0) return;

    this.isLoading = true;
    const result = await this.Api.post<boolean>('Settings/SaveAllLanguageTranslations', updates);
    this.isLoading = false;

    if (result) {
      this.modifiedKeys.clear();
      this.translationsChanged = false;
      await this.getAllTranslations();
      await this.UI.alert("Turbo.CustomerService.ManageTranslations.headSaveSuccess", "Translations saved!", "Turbo.CustomerService.ManageTranslations.msgSaveSuccess", "The translations have been saved.");
    }
    else {
      await this.UI.alert("Turbo.CustomerService.ManageTranslations.headSaveError", "Could not save the changes", "Turbo.CustomerService.ManageTranslations.msgChangeFailedIssues", "There was an error saving the translations. Please try again later.");
    }
  }

  public onTranslationChanged(key: string) {
    this.modifiedKeys.add(key);
    this.translationsChanged = true;
  }

  public areAllLanguagesVerified(row: TranslationInfo): boolean {
    return row.TranslationFields.every(f => f.IsVerified);
  }

  public onRowVerifiedToggle(row: TranslationInfo, isChecked: boolean) {
    row.TranslationFields.forEach(field => {
      field.IsVerified = isChecked;
    });

    this.onTranslationChanged(row.Key);
  }

  public onCommentChanged(row: TranslationInfo) {
    this.modifiedKeys.add(row.Key);
    this.translationsChanged = true;
  }

  async canDeactivate(): Promise<boolean> {
    if (this.translationsChanged) {
      return await this.UI.confirm("Turbo.CustomerService.ManageTranslations.ConfirmLeaving", "Unsaved changes", "Turbo.CustomerService.ManageTranslations.msgConfirmLeaving", "You have unsaved changes. Are you sure you want to leave?");
    }
    return true;
  }
}
